// Full-screen keg scanner shared by every keg-scan screen (packaging, taproom
// orders, invoices, transfers, keg returns, taproom keg count, keg lookup).
import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import QRScanner from './QRScanner';
import { playScanAccept, playScanReject, primeScanAudio } from '../utils/scanFeedback';
import { hasTarget, isComplete, newestFirst, precheckScan, progressLabel } from '../utils/kegScanSession';
import './KegScanOverlay.css';

/** One expected order line (product/size) and how many are scanned so far. */
export interface KegScanLineProgress {
  key: string;
  label: string;
  needed: number;
  scanned: number;
}

/** Optional info returned by the screen for an accepted scan. */
export interface KegScanAccepted {
  message?: string;
  /** 'warn' = accepted but worth a look (e.g. a flagged keg on a return). */
  level?: 'ok' | 'warn';
}

export interface KegScanOverlayProps {
  title: string;
  subtitle?: string;
  /** Tags scanned so far, in scan order (oldest first). Owned by the screen. */
  codes: string[];
  /** Kegs wanted (packaging quantity / order keg count). Omit for open-ended scans. */
  target?: number | null;
  /** Expected order lines, shown so the crew knows what's left. */
  lines?: KegScanLineProgress[];
  /**
   * Hand a scanned tag to the screen. Resolve = accepted (beep), throw an Error
   * = rejected (buzz, red message with the error text).
   */
  onScan: (code: string) => Promise<KegScanAccepted | void> | KegScanAccepted | void;
  onRemove?: (code: string) => void | Promise<void>;
  /** Big Done button: back to the form with the list kept. */
  onDone: () => void;
  doneLabel?: string;
  /** Optional secondary button (e.g. invoice "Cancel" that discards scans). */
  onCancel?: () => void;
  cancelLabel?: string;
  /** Extra content under the progress (e.g. taproom count tally chips). */
  extra?: React.ReactNode;
  /** Optional per-tag detail line in the list (product / size). */
  describe?: (code: string) => React.ReactNode;
}

type Flash = { kind: 'ok' | 'warn' | 'error'; text: string; id: number };

const FLASH_MS = 2600;

const nextTick = () => new Promise<void>((r) => setTimeout(r, 0));

const KegScanOverlay: React.FC<KegScanOverlayProps> = ({
  title,
  subtitle,
  codes,
  target,
  lines,
  onScan,
  onRemove,
  onDone,
  doneLabel = 'Done',
  onCancel,
  cancelLabel = 'Cancel',
  extra,
  describe,
}) => {
  const [manual, setManual] = useState('');
  const [lastCode, setLastCode] = useState<string | null>(null);
  const [flash, setFlash] = useState<Flash | null>(null);
  const [pending, setPending] = useState(0);

  // Latest props for queued camera scans (they can land before a re-render).
  // Sync codes from props in an effect so an optimistic append inside handle()
  // is not wiped by the setState re-render that follows the immediate beep.
  const codesRef = useRef(codes);
  const targetRef = useRef(target);
  targetRef.current = target;
  const onScanRef = useRef(onScan);
  onScanRef.current = onScan;
  const queueRef = useRef<Promise<void>>(Promise.resolve());
  const mountedRef = useRef(true);
  const flashTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const flashId = useRef(0);
  const seenRef = useRef(new Map<string, number>());
  const seqRef = useRef(0);

  useEffect(() => {
    codesRef.current = codes;
  }, [codes]);

  useEffect(() => {
    mountedRef.current = true;
    primeScanAudio();
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      mountedRef.current = false;
      document.body.style.overflow = prevOverflow;
      if (flashTimer.current) clearTimeout(flashTimer.current);
    };
  }, []);

  const show = (kind: Flash['kind'], text: string) => {
    if (!mountedRef.current) return;
    flashId.current += 1;
    setFlash({ kind, text, id: flashId.current });
    if (flashTimer.current) clearTimeout(flashTimer.current);
    flashTimer.current = setTimeout(() => {
      if (mountedRef.current) setFlash(null);
    }, FLASH_MS);
  };

  const reject = (text: string) => {
    playScanReject();
    show('error', text);
  };

  /**
   * Local precheck + beep run immediately when the camera fires so the crew can
   * move to the next keg without waiting on the screen's network round-trip.
   * (Awaiting onScan before playScanAccept was the >2s gap between different
   * kegs on a phone — returns/counts/orders/invoices each hit the API per scan.)
   * The API calls themselves stay serialized so each sees the prior list update.
   */
  const handle = (raw: string, source: 'camera' | 'manual'): Promise<boolean> => {
    if (!mountedRef.current) return Promise.resolve(false);
    const { code, error } = precheckScan(raw, codesRef.current, targetRef.current, source);
    if (error || !code) {
      reject(error || 'Not a keg tag');
      return Promise.resolve(false);
    }

    // Optimistic in-list so a same-tick follow-up scan is treated as a duplicate.
    codesRef.current = [...codesRef.current, code];
    playScanAccept();
    setLastCode(code);
    show('ok', `Added ${code}`);

    let accepted = true;
    const run = async () => {
      await nextTick(); // let the screen's previous state update render
      if (!mountedRef.current) return;
      if (mountedRef.current) setPending((n) => n + 1);
      try {
        const res = (await onScanRef.current(code)) || undefined;
        if (res && res.level === 'warn') show('warn', res.message || `Added ${code}`);
        else if (res && res.message) show('ok', res.message);
      } catch (err: unknown) {
        codesRef.current = codesRef.current.filter((c) => c !== code);
        accepted = false;
        reject(err instanceof Error && err.message ? err.message : `Could not add ${code}`);
      } finally {
        if (mountedRef.current) setPending((n) => Math.max(0, n - 1));
      }
    };
    const p = queueRef.current.then(run, run);
    queueRef.current = p.catch(() => undefined);
    return p.then(() => accepted);
  };

  const submitManual = async () => {
    primeScanAudio();
    const value = manual;
    if (!value.trim()) return;
    const ok = await handle(value, 'manual');
    if (ok && mountedRef.current) setManual('');
  };

  // Newest first by when each tag first showed up in `codes` (screens that
  // group tags by line, e.g. orders, don't keep one scan-ordered list).
  for (const c of codes) {
    if (!seenRef.current.has(c)) seenRef.current.set(c, seqRef.current++);
  }
  const count = codes.length;
  const complete = isComplete(count, target);
  const list = newestFirst(
    [...codes].sort((a, b) => (seenRef.current.get(a) ?? 0) - (seenRef.current.get(b) ?? 0))
  );

  const overlay = (
    <div
      className="kso"
      role="dialog"
      aria-modal="true"
      aria-labelledby="kso-title"
      data-testid="keg-scan-overlay"
      onPointerDown={() => primeScanAudio()}
    >
      <div className="kso__header">
        <div className="kso__titles">
          <h2 id="kso-title" className="kso__title">{title}</h2>
          {subtitle && <p className="kso__subtitle">{subtitle}</p>}
        </div>
        <span
          className={`kso__count${complete ? ' is-complete' : ''}`}
          data-testid="keg-scan-overlay-count"
          aria-live="polite"
        >
          {progressLabel(count, target)}
        </span>
        <button
          type="button"
          className="kso__close"
          aria-label="Close scanner"
          data-testid="keg-scan-overlay-close"
          onClick={onDone}
        >
          ×
        </button>
      </div>

      <div className="kso__camera">
        <QRScanner
          layout="fill"
          showManualToggle={false}
          onScan={(c) => void handle(c, 'camera')}
          onError={(m) => show('error', m)}
        />
        {flash && (
          <div
            key={flash.id}
            className={`kso__flash kso__flash--${flash.kind}`}
            role={flash.kind === 'error' ? 'alert' : 'status'}
            data-testid="keg-scan-overlay-flash"
            data-kind={flash.kind}
          >
            {flash.text}
          </div>
        )}
        {complete && (
          <div className="kso__complete" data-testid="keg-scan-overlay-complete">
            All {target} scanned — tap Done
          </div>
        )}
      </div>

      <div className="kso__body">
        {lines && lines.length > 0 && (
          <ul className="kso__lines" data-testid="keg-scan-overlay-lines">
            {lines.map((l) => {
              const left = Math.max(0, l.needed - l.scanned);
              return (
                <li key={l.key} className={left === 0 ? 'is-complete' : ''} data-testid={`keg-scan-overlay-line-${l.key}`}>
                  <span className="kso__line-label">{l.label}</span>
                  <span className="kso__line-count">
                    {l.scanned} / {l.needed}
                    {left > 0 ? ` · ${left} left` : ' ✓'}
                  </span>
                </li>
              );
            })}
          </ul>
        )}
        {extra}

        <div className="kso__manual">
          <input
            value={manual}
            onChange={(e) => setManual(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                void submitManual();
              }
            }}
            placeholder="Or type keg tag"
            aria-label="Type keg tag"
            data-testid="keg-scan-overlay-input"
            autoComplete="off"
            autoCapitalize="characters"
            inputMode="text"
          />
          <button
            type="button"
            onClick={() => void submitManual()}
            disabled={!manual.trim()}
            data-testid="keg-scan-overlay-add"
          >
            Add
          </button>
        </div>

        {list.length === 0 ? (
          <p className="kso__empty">
            {pending > 0 ? 'Checking…' : hasTarget(target) ? `Scan ${target} keg${target === 1 ? '' : 's'}` : 'Point the camera at a keg tag'}
          </p>
        ) : (
          <ol className="kso__list" data-testid="keg-scan-overlay-list">
            {list.map((c, i) => (
              <li
                key={c}
                className={c === lastCode ? 'is-last' : ''}
                data-testid={`keg-scan-overlay-row-${c}`}
              >
                <span className="kso__num">{count - i}</span>
                <span className="kso__code">
                  <code>{c}</code>
                  {describe && <span className="kso__desc">{describe(c)}</span>}
                </span>
                {onRemove && (
                  <button
                    type="button"
                    className="kso__remove"
                    aria-label={`Remove ${c}`}
                    data-testid={`keg-scan-overlay-remove-${c}`}
                    onClick={() => {
                      if (c === lastCode) setLastCode(null);
                      void Promise.resolve(onRemove(c)).catch((err: unknown) =>
                        show('error', err instanceof Error ? err.message : `Could not remove ${c}`)
                      );
                    }}
                  >
                    ×
                  </button>
                )}
              </li>
            ))}
          </ol>
        )}
      </div>

      <div className="kso__footer">
        {onCancel && (
          <button type="button" className="kso__cancel" onClick={onCancel} data-testid="keg-scan-overlay-cancel">
            {cancelLabel}
          </button>
        )}
        <button type="button" className="kso__done" onClick={onDone} data-testid="keg-scan-overlay-done">
          {doneLabel}
          {hasTarget(target) ? ` (${Math.min(count, target)}/${target})` : count ? ` (${count})` : ''}
        </button>
      </div>
    </div>
  );

  return typeof document !== 'undefined' ? createPortal(overlay, document.body) : overlay;
};

export default KegScanOverlay;
